import { styled } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BookBotelCustomerDetailsForm from '../../components/BookBotelCustomerDetailsForm';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { bookingService } from '../../services/api/bookingService';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const PageContainer = styled('div')({
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(0,0,0,0.7)',
    width: '100%',
    margin: 0,
    padding: 0,
    overflow: 'hidden'
});

const HeroSection = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: 'url(images/filter-background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    minHeight: '100vh',
    padding: theme.breakpoints.down('md') ? '60px 0' : '80px 0',
    flexDirection: 'column',
    margin: 0,
    left: 0,
    right: 0
}));

const BotelSection = styled('div')({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'background.default',
    backgroundImage: 'url(images/background.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: '32px 0',
});

const BotelContainer = styled('div')(({ theme }) => ({
    width: '100%',
    maxWidth: 1200,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.breakpoints.down('md') ? '0 12px' : '0 16px',
}));

const InsuranceSection = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.breakpoints.down('md') ? 24 : 32,
    justifyContent: 'center',
    marginTop: theme.breakpoints.down('md') ? 24 : 32
}));

const InsuranceCard = styled('div')(({ theme }) => ({
    width: '100%',
    background: '#181D23',
    borderRadius: theme.breakpoints.down('md') ? 16 : 20,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    padding: theme.breakpoints.down('md') ? '24px 16px' : '32px',
    color: '#fff'
}));

const InsuranceTitle = styled('h2')(({ theme }) => ({
    textAlign: 'center',
    fontWeight: 600,
    fontSize: theme.breakpoints.down('md') ? 20 : 24,
    marginBottom: theme.breakpoints.down('md') ? 20 : 24
}));

const InsuranceOptions = styled('div')(({ theme }) => ({
    display: 'flex',
    gap: theme.breakpoints.down('md') ? 8 : 10,
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: theme.breakpoints.down('md') ? '10px 10px' : '10px 20px'
}));

const InsuranceOption = styled('div')<{ isPremium?: boolean; isSelected?: boolean }>(({ isPremium, isSelected, theme }) => ({
    flex: 1,
    minWidth: theme.breakpoints.down('md') ? '100%' : 260,
    background: '#10141A',
    borderRadius: theme.breakpoints.down('md') ? 12 : 16,
    padding: theme.breakpoints.down('md') ? '16px 12px' : '24px',
    border: isSelected ? '2px solid #2563EB' : isPremium ? '2px solid #2563EB' : '1px solid #333',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.breakpoints.down('md') ? 6 : 8,
    position: 'relative',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        borderColor: '#2563EB',
        transform: 'translateY(-2px)'
    }
}));

const OptionHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.breakpoints.down('md') ? 6 : 8
}));

const OptionTitle = styled('span')(({ theme }) => ({
    fontWeight: 500,
    fontSize: theme.breakpoints.down('md') ? 16 : 18
}));

const OptionDescription = styled('span')(({ theme }) => ({
    color: '#b0b8c1',
    fontSize: theme.breakpoints.down('md') ? 12 : 14
}));

const OptionPrice = styled('span')(({ theme }) => ({
    fontWeight: 700,
    fontSize: theme.breakpoints.down('md') ? 18 : 20,
    marginTop: theme.breakpoints.down('md') ? 6 : 8
}));

const PremiumBadge = styled('span')(({ theme }) => ({
    background: '#2563EB',
    color: '#fff',
    borderRadius: 8,
    fontSize: theme.breakpoints.down('md') ? 10 : 12,
    padding: '2px 6px',
    marginLeft: theme.breakpoints.down('md') ? 4 : 8
}));

const CrownIcon = styled('span')(({ theme }) => ({
    marginLeft: 'auto',
    background: '#232933',
    borderRadius: '50%',
    width: theme.breakpoints.down('md') ? 24 : 28,
    height: theme.breakpoints.down('md') ? 24 : 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}));

const GuestInfoSection = styled('div')(({ theme }) => ({
    width: '100%',
    background: '#181D23',
    borderRadius: theme.breakpoints.down('md') ? 16 : 20,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    padding: theme.breakpoints.down('md') ? '24px 16px' : '32px',
    color: '#fff'
}));

const GuestInfoCard = styled('div')(({ theme }) => ({
    background: '#232933',
    borderRadius: theme.breakpoints.down('md') ? 12 : 16,
    padding: theme.breakpoints.down('md') ? '20px 16px' : '32px',
    marginTop: theme.breakpoints.down('md') ? 20 : 24
}));

const GuestInfoTitle = styled('h3')(({ theme }) => ({
    textAlign: 'center',
    fontWeight: 600,
    fontSize: theme.breakpoints.down('md') ? 18 : 22,
    marginBottom: theme.breakpoints.down('md') ? 20 : 24
}));

const GuestInfoRow = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: theme.breakpoints.down('sm') ? 'column' : 'row',
    justifyContent: theme.breakpoints.down('sm') ? 'flex-start' : 'space-between',
    marginBottom: theme.breakpoints.down('sm') ? 12 : 16,
    color: '#b0b8c1',
    gap: theme.breakpoints.down('sm') ? 4 : 0
}));

const GuestInfoLabel = styled('span')(({ theme }) => ({
    color: '#b0b8c1',
    fontSize: theme.breakpoints.down('md') ? 14 : 16
}));

const GuestInfoValue = styled('span')(({ theme }) => ({
    color: '#fff',
    fontWeight: 500,
    fontSize: theme.breakpoints.down('md') ? 14 : 16
}));

const GuestInfoPlaceholder = styled('span')(({ theme }) => ({
    color: '#666',
    fontStyle: 'italic',
    fontSize: theme.breakpoints.down('md') ? 14 : 16
}));

const PriceSummary = styled('div')({
    width: '100%',
    margin: '0 auto'
});

const PriceRow = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.breakpoints.down('md') ? 6 : 8,
    color: '#b0b8c1',
    fontSize: theme.breakpoints.down('md') ? 14 : 16
}));

const TotalRow = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 700,
    fontSize: theme.breakpoints.down('md') ? 16 : 18,
    marginBottom: theme.breakpoints.down('md') ? 12 : 16
}));

const Divider = styled('hr')(({ theme }) => ({
    border: 'none',
    borderTop: '1px solid #333',
    margin: theme.breakpoints.down('md') ? '12px 0' : '16px 0'
}));

const PaymentButton = styled('button')(({ theme }) => ({
    width: '100%',
    background: '#2563EB',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: theme.breakpoints.down('md') ? '10px 0' : '12px 0',
    fontWeight: 600,
    fontSize: theme.breakpoints.down('md') ? 14 : 16,
    cursor: 'pointer',
    marginBottom: theme.breakpoints.down('md') ? 10 : 12
}));

const SecureCheckout = styled('div')(({ theme }) => ({
    textAlign: 'center',
    color: '#b0b8c1',
    fontSize: theme.breakpoints.down('md') ? 11 : 13,
    marginTop: theme.breakpoints.down('md') ? 6 : 8
}));

const BookingDetails = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { user } = useAuth();
    const [selectedInsurance, setSelectedInsurance] = useState('premium');
    const [hasOverstayProtection, setHasOverstayProtection] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Get selected botel data from session storage
    const selectedBotelData = JSON.parse(sessionStorage.getItem('selectedBotel') || '{}');
    
    // Add state for guest information
    const [guestInfo, setGuestInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        checkIn: selectedBotelData.checkIn || '',
        checkOut: '',
        guests: 1,
        roomType: ''
    });

    // Ensure baseRate is a number
    const baseRate = typeof selectedBotelData.price === 'number' ? selectedBotelData.price : 89.00;
    const insurancePrice = selectedInsurance === 'premium' ? 5.00 : 2.00;
    const overstayPrice = hasOverstayProtection ? 5.00 : 0.00;
    const taxesAndFees = 9.90;
    const total = baseRate + insurancePrice + overstayPrice + taxesAndFees;

    const handleInsuranceChange = (value: string) => {
        setSelectedInsurance(value);
        showNotification(`Selected ${value === 'premium' ? 'Premium' : 'Basic'} Insurance`, 'info');
    };

    const handleOverstayChange = (checked: boolean) => {
        setHasOverstayProtection(checked);
        showNotification(
            checked ? 'Overstay Protection Added' : 'Overstay Protection Removed',
            'info'
        );
    };

    const handleProceedToPayment = async () => {
        // Check if user is authenticated (token must exist for created_by_user_id to be set)
        const token = localStorage.getItem('token');
        if (!token) {
            showNotification('Please log in to create a booking. Authentication is required.', 'error');
            navigate('/login');
            return;
        }

        // Validate guest information
        if (!guestInfo.firstName || !guestInfo.lastName || !guestInfo.email || !guestInfo.phone) {
            showNotification('Please fill in all required guest information', 'error');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(guestInfo.email)) {
            showNotification('Please enter a valid email address', 'error');
            return;
        }

        // Validate phone number (basic validation)
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!phoneRegex.test(guestInfo.phone)) {
            showNotification('Please enter a valid phone number', 'error');
            return;
        }

        setIsProcessing(true);
        try {
            // Calculate checkout date based on check-in date and duration
            const checkInDate = new Date(selectedBotelData.checkIn);
            const durationHours = parseInt(selectedBotelData.duration.replace('h', ''));
            const checkOutDate = new Date(checkInDate.getTime() + (durationHours * 60 * 60 * 1000));

            // Create the booking using guest booking endpoint
            const booking = await bookingService.createGuestBooking({
                firstName: guestInfo.firstName,
                lastName: guestInfo.lastName,
                email: guestInfo.email,
                phone: guestInfo.phone,
                botelId: selectedBotelData.id,
                checkIn: checkInDate,
                checkOut: checkOutDate,
                totalAmount: total,
                specialRequests: ''
            });

            // Store booking details in session storage for payment page
            sessionStorage.setItem('bookingDetails', JSON.stringify({
                bookingId: booking.id,
                guestInfo,
                selectedInsurance,
                hasOverstayProtection,
                baseRate,
                insurancePrice,
                overstayPrice,
                taxesAndFees,
                total,
                botelId: selectedBotelData.id,
                botelName: selectedBotelData.name,
                checkIn: selectedBotelData.checkIn,
                duration: selectedBotelData.duration
            }));

            showNotification('Booking created successfully! Proceeding to payment...', 'success');
            // Navigate to payment page
            navigate('/payment-details');
        } catch (error) {
            console.error('Failed to create booking:', error);
            
            // Handle specific error for existing users
            if (error instanceof Error && error.message.includes('already exists')) {
                showNotification(
                    `${error.message} Please log in to continue with your booking.`, 
                    'error'
                );
                // Optionally redirect to login page after a delay
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                showNotification('Failed to create booking. Please try again.', 'error');
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const getInsuranceDetails = () => {
        if (selectedInsurance === 'premium') {
            return {
                coverage: '$10,000',
                description: 'Complete Peace Of Mind'
            };
        }
        return {
            coverage: '$1,000',
            description: 'Basic Protection'
        };
    };

    const insuranceDetails = getInsuranceDetails();

    return (
        <PageContainer>
            <HeroSection>
                <Header />
                <BookBotelCustomerDetailsForm 
                    initialValues={guestInfo}
                    onChange={setGuestInfo}
                />
            </HeroSection>

            <BotelSection>
                <BotelContainer>
                    <InsuranceSection>
                        <InsuranceCard>
                            <InsuranceTitle>Peace Of Mind Insurance</InsuranceTitle>
                            <InsuranceOptions>
                                <InsuranceOption 
                                    isSelected={selectedInsurance === 'basic'}
                                    onClick={() => handleInsuranceChange('basic')}
                                >
                                    <OptionHeader>
                                        <input 
                                            type="radio" 
                                            name="insurance" 
                                            value="basic" 
                                            checked={selectedInsurance === 'basic'}
                                            onChange={(e) => handleInsuranceChange(e.target.value)}
                                            style={{ accentColor: '#2563EB' }} 
                                        />
                                        <OptionTitle>
                                            Basic Insurance <span title="Covers up to $1,000" style={{ color: '#b0b8c1', marginLeft: 4, fontSize: 16, cursor: 'pointer' }}>ⓘ</span>
                                        </OptionTitle>
                                    </OptionHeader>
                                    <OptionDescription>$1,000 Coverage For Basic Protection</OptionDescription>
                                    <OptionPrice>$2</OptionPrice>
                                </InsuranceOption>

                                <InsuranceOption 
                                    isPremium={selectedInsurance === 'premium'}
                                    isSelected={selectedInsurance === 'premium'}
                                    onClick={() => handleInsuranceChange('premium')}
                                >
                                    <OptionHeader>
                                        <input 
                                            type="radio" 
                                            name="insurance" 
                                            value="premium" 
                                            checked={selectedInsurance === 'premium'}
                                            onChange={(e) => handleInsuranceChange(e.target.value)}
                                            style={{ accentColor: '#2563EB' }} 
                                        />
                                        <OptionTitle>
                                            Premium Insurance <span title="Covers up to $10,000" style={{ color: '#b0b8c1', marginLeft: 4, fontSize: 16, cursor: 'pointer' }}>ⓘ</span>
                                        </OptionTitle>
                                        <PremiumBadge>Recommended</PremiumBadge>
                                        <CrownIcon>
                                            <span role="img" aria-label="crown">👑</span>
                                        </CrownIcon>
                                    </OptionHeader>
                                    <OptionDescription>$10,000 Coverage For Complete Peace Of Mind</OptionDescription>
                                    <OptionPrice>$5</OptionPrice>
                                </InsuranceOption>
                            </InsuranceOptions>

                            <InsuranceOptions>
                                <InsuranceOption 
                                    isSelected={hasOverstayProtection}
                                    onClick={() => handleOverstayChange(!hasOverstayProtection)}
                                >
                                    <OptionHeader>
                                        <input 
                                            type="checkbox" 
                                            name="overstay" 
                                            checked={hasOverstayProtection}
                                            onChange={(e) => handleOverstayChange(e.target.checked)}
                                            style={{ accentColor: '#2563EB' }} 
                                        />
                                        <OptionTitle>
                                            Overstay Protection <span title="Add flexibility to your booking" style={{ color: '#b0b8c1', marginLeft: 4, fontSize: 16, cursor: 'pointer' }}>ⓘ</span>
                                        </OptionTitle>
                                    </OptionHeader>
                                    <OptionDescription>Add Flexibility To Your Booking</OptionDescription>
                                    <OptionPrice>$5</OptionPrice>
                                </InsuranceOption>
                            </InsuranceOptions>
                        </InsuranceCard>

                        <GuestInfoSection>
                            <GuestInfoTitle>Booking Details</GuestInfoTitle>

                            <PriceSummary>
                                <PriceRow>
                                    <span>Base Rate</span>
                                    <span>${baseRate.toFixed(2)}</span>
                                </PriceRow>
                                <PriceRow>
                                    <span>{selectedInsurance === 'premium' ? 'Premium Insurance' : 'Basic Insurance'}</span>
                                    <span>${insurancePrice.toFixed(2)}</span>
                                </PriceRow>
                                {hasOverstayProtection && (
                                    <PriceRow>
                                        <span>Overstay Protection</span>
                                        <span>${overstayPrice.toFixed(2)}</span>
                                    </PriceRow>
                                )}
                                <PriceRow>
                                    <span>Taxes & Fees</span>
                                    <span>${taxesAndFees.toFixed(2)}</span>
                                </PriceRow>
                                <Divider />
                                <TotalRow>
                                    <span>Total</span>
                                    <span>${total.toFixed(2)}</span>
                                </TotalRow>
                                <PaymentButton onClick={handleProceedToPayment} disabled={isProcessing}>
                                    {isProcessing ? 'Processing...' : 'Proceed To Payment'}
                                </PaymentButton>
                                <SecureCheckout>
                                    <span role="img" aria-label="lock">🔒</span> Secure Checkout Powered By Stripe
                                </SecureCheckout>
                            </PriceSummary>
                        </GuestInfoSection>
                    </InsuranceSection>
                </BotelContainer>
            </BotelSection>
            <Footer />
        </PageContainer>
    );
};

export default BookingDetails; 